import { useGetResidenceStatusList } from "@/api/services/global/master";
import { MAX_SPECIFIC_SKILL_TRANSITION_MONTH_PERIOD, MAX_SPECIFIC_SKILL_TRANSITION_YEAR_PERIOD } from "@/constants/common";
import { INVALID_NUMBER_MAX, INVALID_NUMBER_MIN } from "@/constants/invalids";
import { SPECIFIC_SKILL_TRANSITION_PERIOD_ERROR_MESSAGE } from "@/features/employee/constants/employeeForm";
import { caculateSpecificSkillTransitionPeriod } from "@/features/employee/utils/caculateSpecificSkillTransitionPeriod";
import { EEmployeeCareerJapaneseCertification, EEmployeeCareerSkillCertificationOne } from "@/features/renew-visa/enums/employeeApplicationForm";
import { EMPLOYEE_CAREER_LOCATION, EMPLOYMENT_STATUS, RETIREMENT_STATUS } from "@/types/enum";
import { FULL_WIDTH_CHARACTER_OPTION, HALF_WIDTH_CHARACTER_OPTION, JAPANESE_CHARACTER_OPTION } from "@/utils/cp932Validator";
import { toNumber } from "@/utils/number";
import { replaceStr } from "@/utils/string";
import { validator } from "@/utils/validator";

export const useGetEmployeeSchema = () => {
  const { residenceStatusOptions } = useGetResidenceStatusList();

  const employeeSchema = validator.object().shape({
    // info form
    managementNumber: validator.string().trim().emptyToNull().autoTransformToHalfWidth().label("管理番号").notRequired(),
    lastName: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .latinsOnly()
      .autoTransformToUpperCase()
      .label("姓")
      .notRequired()
      .test("combined-name-length", "「姓」「ミドルネーム」「名」の合計は104文字以内にしてください。", function (value) {
        const { firstName, middleName } = this.parent;
        const totalLength = (value || "").length + (middleName || "").length + (firstName || "").length;
        return totalLength <= 104;
      }),
    middleName: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .latinsOnly()
      .autoTransformToUpperCase()
      .label("ミドルネーム")
      .notRequired(),
    firstName: validator.string().trim().emptyToNull().autoTransformToHalfWidth().latinsOnly().autoTransformToUpperCase().label("名").required(),
    nickName: validator.string().trim().emptyToNull().max(104).label("呼び名").notRequired(),
    nationalityId: validator.string().isSelection().trim().emptyToNull().label("国籍").required(),
    birthday: validator.mixed().isDayjs().isSelection().isBeforeAge(18).label("生年月日").required(),
    gender: validator.string().isSelection().trim().emptyToNull().label("性別").required(),
    employmentStatus: validator.string().trim().isSelection().emptyToNull().label("在籍ステータス").required(),
    hiringDate: validator
      .mixed()
      .isDayjs()
      .isSelection()
      .isBeforeDateOfField("retirementDate", { startLabel: "入社日", endLabel: "退職日" })
      .label("入社日（予定日）")
      .required(),
    skillOneStartDate: validator.mixed().isDayjs().isSelection().isPastOrToday().label("特定技能1号の起算日").notRequired(),
    blankPeriodMonthly: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .numbersOnly()
      .min(0, replaceStr(INVALID_NUMBER_MIN, { path: "ブランク期間（月単位）", min: 0 }))
      .maxValue(600, replaceStr(INVALID_NUMBER_MAX, { path: "ブランク期間（月単位）", max: 600 }))
      .label("ブランク期間（月単位）")
      .notRequired(),
    specificSkillTransitionPeriod: validator
      .string()
      .trim()
      .emptyToNull()
      .label("特定技能経過期間")
      .notRequired()
      .when(["skillOneStartDate", "blankPeriodMonthly"], ([skillOneStartDate, blankPeriodMonthly], schema) => {
        return schema.test("checkSpecificSkillTransitionPeriod", "", function () {
          const { path, createError } = this;
          const { monthPeriod, yearPeriod } = caculateSpecificSkillTransitionPeriod({
            skillOneStartDate: skillOneStartDate,
            blankPeriodMonthly: blankPeriodMonthly?.toString() || "0",
          });
          if (
            yearPeriod > MAX_SPECIFIC_SKILL_TRANSITION_YEAR_PERIOD ||
            (yearPeriod === MAX_SPECIFIC_SKILL_TRANSITION_YEAR_PERIOD && monthPeriod > MAX_SPECIFIC_SKILL_TRANSITION_MONTH_PERIOD)
          ) {
            return createError({ path, message: () => SPECIFIC_SKILL_TRANSITION_PERIOD_ERROR_MESSAGE });
          } else {
            return true;
          }
        });
      }),
    retirementStatus: validator.string().isSelection().trim().emptyToNull().label("退職ステータス").notRequired(),
    retirementDate: validator
      .mixed()
      .isDayjs()
      .isAfterDateOfField("hiringDate", { startLabel: "入社日", endLabel: "退職日" })
      .label("退職日（予定日）")
      .notRequired()
      .when("retirementStatus", {
        is: RETIREMENT_STATUS.RETIRED,
        then: () =>
          validator
            .mixed()
            .isSelection()
            .isDayjs()
            .isAfterDateOfField("hiringDate", { startLabel: "入社日", endLabel: "退職日" })
            .label("退職日（予定日）")
            .required(),
      }),
    hasRetiredBefore: validator.bool().notRequired(),
    zipCode: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .zipCode()
      .label("郵便番号")
      .when("employmentStatus", {
        is: EMPLOYMENT_STATUS.WORKING,
        then: (schema) => schema.required(),
      })
      .when("employmentStatus", {
        is: EMPLOYMENT_STATUS.LEFT,
        then: (schema) => schema.required(),
      })
      .notRequired(),
    province: validator.string().trim().isSelection().emptyToNull().label("都道府県").notRequired(),
    municipality: validator.string().isSelection().trim().emptyToNull().label("市区町村").notRequired(),
    address: validator
      .string()
      .trim()
      .emptyToNull()
      .max(80)
      .autoTransformToFullWidth()
      .label("都道府県・市区町村以外の住所")
      .isValidCP932Character({ ...HALF_WIDTH_CHARACTER_OPTION, ...FULL_WIDTH_CHARACTER_OPTION, ...JAPANESE_CHARACTER_OPTION })
      .when("employmentStatus", {
        is: EMPLOYMENT_STATUS.WORKING,
        then: (schema) => schema.required(),
      })
      .when("employmentStatus", {
        is: EMPLOYMENT_STATUS.LEFT,
        then: (schema) => schema.required(),
      })
      .notRequired(),
    telNumber: validator.string().trim().emptyToNull().autoTransformToHalfWidth().isTelNumber().label("電話番号").notRequired(),
    email: validator.string().trim().emptyToNull().autoTransformToHalfWidth().email().max(60).label("メールアドレス").required(),
    referralCompanyName: validator.string().trim().emptyToNull().label("紹介会社名").notRequired(),
    registeredSupportOrganizationName: validator.string().trim().emptyToNull().label("登録支援機関名").notRequired(),

    // passport
    passportPermanentAddress: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToFullWidth()
      .max(80)
      .isValidCP932Character({ ...HALF_WIDTH_CHARACTER_OPTION, ...FULL_WIDTH_CHARACTER_OPTION, ...JAPANESE_CHARACTER_OPTION })
      .label("本国における居住地")
      .notRequired(),
    passportCode: validator
      .string()
      .trim()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .max(9)
      .uppercaseAlphanumericCharactersOnly()
      .label("旅券番号")
      .notRequired()
      .test((value, ctx) => {
        if (ctx.parent.passportExpiryDate && !ctx.parent.passportCode) return ctx.createError({ message: "「旅券番号」を入力してください。" });
        return true;
      }),
    passportExpiryDate: validator
      .mixed()
      .isSelection()
      .isDayjs()
      .label("旅券有効期限")
      .notRequired()
      .test((value, ctx) => {
        if (!ctx.parent.passportExpiryDate && ctx.parent.passportCode) return ctx.createError({ message: "「旅券有効期限」を選択してください。" });
        return true;
      }),
    passportFile: validator.mixed().isExtendFile().label("パスポート写しデータ（1枚まで）").notRequired(),
    // residence card
    residenceCode: validator
      .string()
      .trim()
      .emptyToNull()
      .length(12)
      .uppercaseAlphanumericCharactersOnly()
      .autoTransformToHalfWidth()
      .label("在留カード番号")
      .when("employmentStatus", {
        is: EMPLOYMENT_STATUS.WORKING,
        then: (schema) => schema.required(),
      })
      .when("employmentStatus", {
        is: EMPLOYMENT_STATUS.LEFT,
        then: (schema) => schema.required(),
      })
      .notRequired(),
    residenceStatusId: validator.string().trim().isSelection().emptyToNull().label("在留資格").required(),
    residencePeriodId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("在留期間")
      .notRequired()
      .when(["residenceCode", "residenceStatusId"], ([residenceCode, residenceStatusId], schema) => {
        const residenceStatusLabel = residenceStatusOptions.find((item) => toNumber(item.value) === toNumber(residenceStatusId))?.label;
        if (!!residenceCode && !!residenceStatusLabel && !["永住者", "未取得"].includes(residenceStatusLabel)) {
          return schema.required();
        }
        return schema;
      }),
    residenceExpiryDate: validator
      .mixed()
      .isSelection()
      .isDayjs()
      .label("在留期限（在留期間満了日）")
      .notRequired()
      .when(["residenceCode", "residenceStatusId"], ([residenceCode, residenceStatusId], schema) => {
        if (!!residenceCode && residenceStatusOptions.find((item) => toNumber(item.value) === toNumber(residenceStatusId))?.label !== "永住者") {
          return schema.required();
        }
        return schema;
      }),
    residenceCardFrontFile: validator.array().of(validator.mixed().isExtendFile()).label("在留カード写し表データ（10枚まで）").notRequired(),
    residenceCardBackFile: validator.array().of(validator.mixed().isExtendFile()).label("在留カード写し裏データ（10枚まで）").notRequired(),

    // contract
    contractEmploymentFile: validator.array().of(validator.mixed().isExtendFile()).label("雇用契約書（5部まで）").notRequired(),
    contractConditionFile: validator.array().of(validator.mixed().isExtendFile()).label("雇用条件書・賃金の支払（5部まで）").notRequired(),
    contractCompanyBranchId: validator.string().isSelection().trim().emptyToNull().label("勤務先事業所").required(),
    contractStartDate: validator
      .mixed()
      .isSelection()
      .isDayjs()
      .isBeforeDateOfField("contractEndDate", { startLabel: "雇用契約期間（始期）", endLabel: "雇用契約期間（終期）" })
      .label("雇用契約期間（始期）")
      .notRequired(),
    contractEndDate: validator
      .mixed()
      .isSelection()
      .isDayjs()
      .isAfterDateOfField("contractStartDate", { startLabel: "雇用契約期間（始期）", endLabel: "雇用契約期間（終期）" })
      .label("雇用契約期間（終期）")
      .notRequired(),
    contractMainOccupationId: validator.string().isSelection().trim().emptyToNull().label("主たる職種").required(),
    contractSpecificBusinessClassificationOneId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("特定産業分野・業務区分 1")
      .when("residenceStatusId", {
        is: (value: unknown) => !value,
        then: (schema) => schema.required("特定産業分野・業務区分を選択する前に、在留資格を選択してください。"),
      })
      .when("residenceStatusId", {
        is: "1",
        then: (schema) => schema.required(),
      })
      .when("residenceStatusId", {
        is: "2",
        then: (schema) => schema.required(),
      })
      .notRequired(),
    contractSpecificBusinessClassificationTwoId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("特定産業分野・業務区分 2")
      .when("residenceStatusId", {
        is: (value: unknown) => !value,
        then: (schema) => schema.required("特定産業分野・業務区分を選択する前に、在留資格を選択してください。"),
      })
      .notRequired(),
    contractSpecificBusinessClassificationThreeId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("特定産業分野・業務区分 3")
      .when("residenceStatusId", {
        is: (value: unknown) => !value,
        then: (schema) => schema.required("特定産業分野・業務区分を選択する前に、在留資格を選択してください。"),
      })
      .notRequired(),
    contractMonthlySalary: validator
      .number()
      .commas()
      .emptyToNull()
      .autoTransformToHalfWidth()
      .integer()
      .min(0)
      .max(99999999)
      .label("月額報酬（円）")
      .notRequired(),

    // career
    careerResumeFile: validator.mixed().isExtendFile().label("履歴書").notRequired(),
    careerTrainingStatus: validator.string().trim().emptyToNull().label("技能実習2号修了の有無").notRequired(),
    careerMajorId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("職種")
      .notRequired()
      .when("careerSkillCertificationOne", {
        is: (value: unknown) => value === EEmployeeCareerSkillCertificationOne.COMPLETED_TECHNICAL_INTERN_TRAINING,
        then: (schema) => schema.required(),
      })
      .when("careerJapaneseCertificationOne", {
        is: (value: unknown) => value === EEmployeeCareerJapaneseCertification.COMPLETED_TECHNICAL_INTERN_TRAINING,
        then: (schema) => schema.required(),
      }),
    careerJobTypeId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("作業")
      .notRequired()
      .when("careerSkillCertificationOne", {
        is: (value: unknown) => value === EEmployeeCareerSkillCertificationOne.COMPLETED_TECHNICAL_INTERN_TRAINING,
        then: (schema) => schema.required(),
      })
      .when("careerJapaneseCertificationOne", {
        is: (value: unknown) => value === EEmployeeCareerJapaneseCertification.COMPLETED_TECHNICAL_INTERN_TRAINING,
        then: (schema) => schema.required(),
      }),
    careerCompletionProof: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("良好に修了したことの証明")
      .notRequired()
      .when("careerSkillCertificationOne", {
        is: (value: unknown) => value === EEmployeeCareerSkillCertificationOne.COMPLETED_TECHNICAL_INTERN_TRAINING,
        then: (schema) => schema.required(),
      })
      .when("careerJapaneseCertificationOne", {
        is: (value: unknown) => value === EEmployeeCareerJapaneseCertification.COMPLETED_TECHNICAL_INTERN_TRAINING,
        then: (schema) => schema.required(),
      }),
    careerTrainingCompletionFile: validator.mixed().isExtendFile().label("修了の証明").notRequired(),
    careerSkillCertificationOne: validator.string().isSelection().trim().emptyToNull().label("技能水準　証明1").notRequired(),
    careerSkillExamOneId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("合格した試験名1（技能）")
      .notRequired()
      .when(["careerSkillOtherEvaluation", "careerSkillCertificationOne"], ([careerSkillOtherEvaluation, careerSkillCertificationOne], schema) => {
        return schema.test("checkCareerSkillExamOneId", "", function (value) {
          const { path, createError } = this;
          if (
            careerSkillCertificationOne === EEmployeeCareerJapaneseCertification.FIELD_SPECIFIC_OPERATION_POLICY &&
            !(careerSkillOtherEvaluation || value)
          ) {
            return createError({ path, message: () => "合格した試験名1（技能）またはその他の評価方法による証明を入力してください。" });
          } else {
            return true;
          }
        });
      }),
    careerSkillExamLocationOne: validator.string().trim().emptyToNull().label("受験地1").notRequired(),
    careerSkillExamOverseaOneNationalityId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("技能受験地1（国外）")
      .notRequired()
      .when(["careerSkillExamLocationOne"], ([careerSkillExamLocationOne], schema) => {
        if (careerSkillExamLocationOne === EMPLOYEE_CAREER_LOCATION.OUTSIDE_JAPAN) {
          return schema.required();
        }
        return schema;
      }),
    careerSkillOtherEvaluation: validator
      .string()
      .trim()
      .emptyToNull()
      .max(60)
      .isValidCP932Character({ ...HALF_WIDTH_CHARACTER_OPTION, ...FULL_WIDTH_CHARACTER_OPTION, ...JAPANESE_CHARACTER_OPTION })
      .label("その他の評価方法による証明")
      .notRequired(),
    careerSkillCompletionFile: validator.mixed().isExtendFile().label("合格証明書1").notRequired(),
    careerJapaneseCertificationOne: validator.string().isSelection().trim().emptyToNull().label("日本語能力　証明1").notRequired(),
    careerJapaneseExamOneId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("合格した試験名1（日本語）")
      .notRequired()
      .when("careerJapaneseCertificationOne", {
        is: EEmployeeCareerJapaneseCertification.FIELD_SPECIFIC_OPERATION_POLICY,
        then: (schema) => schema.required(),
      }),
    careerJapaneseExamLocationOne: validator.string().trim().emptyToNull().label("受験地1").notRequired(),
    careerJapaneseExamOverseaOneNationalityId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("日本語受験地1（国外）")
      .notRequired()
      .when(["careerJapaneseExamLocationOne"], ([careerJapaneseExamLocationOne], schema) => {
        if (careerJapaneseExamLocationOne === EMPLOYEE_CAREER_LOCATION.OUTSIDE_JAPAN) {
          return schema.required();
        }
        return schema;
      }),
    careerJanpaneseCompletionOneFile: validator.mixed().isExtendFile().label("合格証明書1").notRequired(),
    careerJapaneseCertificationTwo: validator.string().isSelection().trim().emptyToNull().label("日本語能力　証明2").notRequired(),
    careerJapaneseExamTwoId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("合格した試験名2（日本語）")
      .notRequired()
      .when("careerJapaneseCertificationTwo", {
        is: EEmployeeCareerJapaneseCertification.FIELD_SPECIFIC_OPERATION_POLICY,
        then: (schema) => schema.required(),
      }),
    careerJapaneseExamLocationTwo: validator.string().trim().emptyToNull().label("受験地2").notRequired(),
    careerJapaneseExamOverseaTwoNationalityId: validator
      .string()
      .isSelection()
      .trim()
      .emptyToNull()
      .label("日本語受験地2（国外）")
      .notRequired()
      .when(["careerJapaneseExamLocationTwo"], ([careerJapaneseExamLocationTwo], schema) => {
        if (careerJapaneseExamLocationTwo === EMPLOYEE_CAREER_LOCATION.OUTSIDE_JAPAN) {
          return schema.required();
        }
        return schema;
      }),
    careerJanpaneseCompletionTwoFile: validator.mixed().isExtendFile().label("合格証明書2").notRequired(),
    careerConfirmationGuidanceFile: validator.mixed().isExtendFile().label("事前ガイダンスの確認書").notRequired(),
    careerConfirmationOrientationFile: validator.mixed().isExtendFile().label("生活オリエンテーションの確認書").notRequired(),
    careerDependentNote: validator.string().trim().emptyToNull().label("扶養する家族などメモ").notRequired(),
    careerDependentFamilyFile: validator
      .array()
      .of(validator.mixed().isExtendFile())
      .label("親族関係書類（戸籍謄本など）・翻訳（5枚まで）")
      .notRequired(),
    careerDependentRemittanceFile: validator.array().of(validator.mixed().isExtendFile()).label("送金関係書類（5枚まで）").notRequired(),
    note: validator.string().trim().emptyToNull().label("備考").notRequired(),
  });

  const employeeDraftSchema = validator.object().shape({
    // info form
    managementNumber: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    lastName: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    middleName: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    firstName: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    nickName: validator.string().trim().emptyToNull().nullable(),
    nationalityId: validator.string().isSelection().trim().emptyToNull().nullable(),
    birthday: validator.mixed().isDayjs().isSelection().nullable(),
    gender: validator.string().isSelection().trim().emptyToNull().nullable(),
    employmentStatus: validator.string().trim().isSelection().emptyToNull().nullable(),
    hiringDate: validator.mixed().isDayjs().isSelection().nullable(),
    skillOneStartDate: validator.mixed().isDayjs().isSelection().nullable(),
    blankPeriodMonthly: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    specificSkillTransitionPeriod: validator.string().trim().emptyToNull().nullable(),
    retirementStatus: validator.string().isSelection().trim().emptyToNull().nullable(),
    retirementDate: validator.mixed().isDayjs().nullable(),
    hasRetiredBefore: validator.bool().nullable(),
    zipCode: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    province: validator.string().trim().isSelection().emptyToNull().nullable(),
    municipality: validator.string().isSelection().trim().emptyToNull().nullable(),
    address: validator.string().trim().emptyToNull().autoTransformToFullWidth().nullable(),
    telNumber: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    email: validator.string().trim().emptyToNull().autoTransformToHalfWidth().email().nullable(),
    referralCompanyName: validator.string().trim().emptyToNull().label("紹介会社名").nullable(),
    registeredSupportOrganizationName: validator.string().trim().emptyToNull().nullable(),

    // passport
    passportPermanentAddress: validator.string().trim().emptyToNull().autoTransformToFullWidth().nullable(),
    passportCode: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),

    passportExpiryDate: validator.mixed().isSelection().isDayjs().nullable(),
    passportFile: validator.mixed().isExtendFile().nullable(),
    // residence card
    residenceCode: validator.string().trim().emptyToNull().autoTransformToHalfWidth().nullable(),
    residenceStatusId: validator.string().trim().isSelection().emptyToNull().nullable(),
    residencePeriodId: validator.string().isSelection().trim().emptyToNull().nullable(),
    residenceExpiryDate: validator.mixed().isSelection().isDayjs().nullable(),
    residenceCardFrontFile: validator.array().of(validator.mixed().isExtendFile()).nullable(),
    residenceCardBackFile: validator.array().of(validator.mixed().isExtendFile()).nullable(),

    // contract
    contractEmploymentFile: validator.array().of(validator.mixed().isExtendFile()).nullable(),
    contractConditionFile: validator.array().of(validator.mixed().isExtendFile()).nullable(),
    contractCompanyBranchId: validator.string().isSelection().trim().emptyToNull().nullable(),
    contractStartDate: validator.mixed().isSelection().isDayjs().nullable(),
    contractEndDate: validator.mixed().isSelection().isDayjs().nullable(),
    contractMainOccupationId: validator.string().isSelection().trim().emptyToNull().nullable(),
    contractSpecificBusinessClassificationOneId: validator.string().isSelection().trim().emptyToNull().nullable(),
    contractSpecificBusinessClassificationTwoId: validator.string().isSelection().trim().emptyToNull().nullable(),
    contractSpecificBusinessClassificationThreeId: validator.string().isSelection().trim().emptyToNull().nullable(),
    contractMonthlySalary: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable(),

    // career
    careerResumeFile: validator.mixed().isExtendFile().nullable(),
    careerTrainingStatus: validator.string().trim().emptyToNull().nullable(),
    careerMajorId: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerJobTypeId: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerCompletionProof: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerTrainingCompletionFile: validator.mixed().isExtendFile().nullable(),
    careerSkillCertificationOne: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerSkillExamOneId: validator.string().isSelection().trim().emptyToNull().nullable(),

    careerSkillExamLocationOne: validator.string().trim().emptyToNull().nullable(),
    careerSkillExamOverseaOneNationalityId: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerSkillOtherEvaluation: validator.string().trim().emptyToNull().nullable(),
    careerSkillCompletionFile: validator.mixed().isExtendFile().nullable(),
    careerJapaneseCertificationOne: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerJapaneseExamOneId: validator.string().isSelection().trim().emptyToNull().nullable(),

    careerJapaneseExamLocationOne: validator.string().trim().emptyToNull().nullable(),
    careerJapaneseExamOverseaOneNationalityId: validator.string().isSelection().trim().emptyToNull().nullable(),

    careerJanpaneseCompletionOneFile: validator.mixed().isExtendFile().nullable(),
    careerJapaneseCertificationTwo: validator.string().isSelection().trim().emptyToNull().nullable(),
    careerJapaneseExamTwoId: validator.string().isSelection().trim().emptyToNull().nullable(),

    careerJapaneseExamLocationTwo: validator.string().trim().emptyToNull().nullable(),
    careerJapaneseExamOverseaTwoNationalityId: validator.string().isSelection().trim().emptyToNull().nullable(),

    careerJanpaneseCompletionTwoFile: validator.mixed().isExtendFile().nullable(),
    careerConfirmationGuidanceFile: validator.mixed().isExtendFile().nullable(),
    careerConfirmationOrientationFile: validator.mixed().isExtendFile().nullable(),
    careerDependentNote: validator.string().trim().emptyToNull().nullable(),
    careerDependentFamilyFile: validator.array().of(validator.mixed().isExtendFile()).nullable(),
    careerDependentRemittanceFile: validator.array().of(validator.mixed().isExtendFile()).nullable(),
    note: validator.string().trim().emptyToNull().nullable(),
  });

  return { employeeSchema, employeeDraftSchema };
};
